/**
 * 执行中心 — 查看 Agent 执行历史与状态（真实 API）
 * GET /api/agents/recent — 用户最近执行记录
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { fetchAgents, AgentDTO } from '../../api/agent';
import { fetchRecentExecutions, ExecutionDTO } from '../../api/execution';

export default function ExecutionCenterPage() {
  const navigate = useNavigate();
  const [agents, setAgents] = useState<AgentDTO[]>([]);
  const [executions, setExecutions] = useState<ExecutionDTO[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      fetchAgents({ page: 0, size: 5 }).catch(() => ({ items: [] as AgentDTO[] })),
      fetchRecentExecutions({ page: 0, size: 20 }).catch(() => ({ items: [] as ExecutionDTO[], total: 0, page: 0, size: 20 })),
    ]).then(([agentsRes, execsRes]) => {
      setAgents(agentsRes.items || []);
      setExecutions(execsRes.items || []);
    }).finally(() => setLoading(false));
  }, []);

  const statusCfg: Record<string, { label: string; color: string; bg: string }> = {
    COMPLETED: { label: '成功', color: '#34d399', bg: 'rgba(52,211,153,0.1)' },
    RUNNING: { label: '执行中', color: '#60a5fa', bg: 'rgba(96,165,250,0.1)' },
    TOOL_CALLING: { label: '执行中', color: '#60a5fa', bg: 'rgba(96,165,250,0.1)' },
    PLANNING: { label: '执行中', color: '#60a5fa', bg: 'rgba(96,165,250,0.1)' },
    PENDING: { label: '排队中', color: '#fbbf24', bg: 'rgba(251,191,36,0.1)' },
    FAILED: { label: '失败', color: '#f87171', bg: 'rgba(248,113,113,0.1)' },
    TIMEOUT: { label: '超时', color: '#f87171', bg: 'rgba(248,113,113,0.1)' },
    CANCELLED: { label: '已取消', color: '#9ca3af', bg: 'rgba(156,163,175,0.1)' },
  };

  const totalExecs = executions.length;
  const successCount = executions.filter(e => e.status === 'COMPLETED').length;
  const avgDuration = executions.length > 0
    ? (executions.reduce((sum, e) => sum + (e.durationMs || 0), 0) / executions.length / 1000).toFixed(1) + 's'
    : '0s';

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h1 style={styles.title}>⚡ 执行中心</h1>
        <p style={styles.subtitle}>查看 Agent 执行历史、监控运行状态</p>
      </div>

      {/* Quick Stats */}
      <div style={styles.stats}>
        <div style={styles.statCard}>
          <span style={styles.statEmoji}>📊</span>
          <div>
            <span style={styles.statVal}>{totalExecs}</span>
            <span style={styles.statLbl}>总执行次数</span>
          </div>
        </div>
        <div style={styles.statCard}>
          <span style={styles.statEmoji}>✅</span>
          <div>
            <span style={styles.statVal}>{successCount}</span>
            <span style={styles.statLbl}>成功</span>
          </div>
        </div>
        <div style={styles.statCard}>
          <span style={styles.statEmoji}>⚡</span>
          <div>
            <span style={styles.statVal}>{avgDuration}</span>
            <span style={styles.statLbl}>平均耗时</span>
          </div>
        </div>
        <div style={styles.statCard}>
          <span style={styles.statEmoji}>🤖</span>
          <div>
            <span style={styles.statVal}>{agents.length}</span>
            <span style={styles.statLbl}>活跃 Agent</span>
          </div>
        </div>
      </div>

      {/* Recent Executions */}
      <div>
        <h2 style={styles.sectionTitle}>最近执行</h2>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 30, color: '#64748b', fontSize: 12 }}>加载中...</div>
        ) : executions.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 30, color: '#64748b', fontSize: 12 }}>
            暂无执行记录，去 <a onClick={() => navigate('/simon-cat/agents')} style={{ color: '#a5b4fc', cursor: 'pointer' }}>创建 Agent</a> 开始体验
          </div>
        ) : (
          <div style={styles.execList}>
            {executions.map((exec) => {
              const cfg = statusCfg[exec.status || 'COMPLETED'] || statusCfg.COMPLETED;
              return (
                <div key={exec.executionId} style={styles.execItem} onClick={() => navigate(`/simon-cat/agents/${exec.agentId}`)}>
                  <span style={styles.execAvatar}>🤖</span>
                  <div style={styles.execInfo}>
                    <span style={styles.execName}>{exec.agentId}</span>
                    <span style={styles.execMeta}>
                      {exec.totalTokens ? `${exec.totalTokens} tokens · ` : ''}{exec.createdAt || ''}
                    </span>
                  </div>
                  <span style={styles.execDuration}>{exec.durationMs ? `${(exec.durationMs / 1000).toFixed(1)}s` : '-'}</span>
                  <span style={{
                    ...styles.execStatus,
                    color: cfg.color,
                    background: cfg.bg,
                  }}>
                    {cfg.label}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  page: { maxWidth: 900, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 24 },
  header: {},
  title: { fontSize: 22, fontWeight: 700, margin: 0 },
  subtitle: { fontSize: 12, color: '#64748b', margin: '4px 0 0' },
  stats: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 },
  statCard: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '14px 16px', borderRadius: 10, background: 'rgba(255,255,255,0.025)',
    border: '1px solid rgba(255,255,255,0.06)',
  },
  statEmoji: { fontSize: 20 },
  statVal: { display: 'block', fontSize: 16, fontWeight: 700 },
  statLbl: { display: 'block', fontSize: 10, color: '#64748b' },
  sectionTitle: { fontSize: 15, fontWeight: 600, margin: '0 0 10px' },
  execList: { display: 'flex', flexDirection: 'column', gap: 6 },
  execItem: {
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '12px 14px', borderRadius: 10, background: 'rgba(255,255,255,0.025)',
    border: '1px solid rgba(255,255,255,0.06)', cursor: 'pointer',
    transition: 'background 0.15s',
  },
  execAvatar: { fontSize: 18 },
  execInfo: { flex: 1, display: 'flex', flexDirection: 'column', gap: 2 },
  execName: { fontSize: 13, fontWeight: 600 },
  execMeta: { fontSize: 10, color: '#64748b' },
  execDuration: { fontSize: 11, fontFamily: 'monospace', color: '#94a3b8' },
  execStatus: { fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 4 },
};
